import { Params } from '@angular/router';
import { IgComment, IgUser } from '@core/models/ApiFields/ig-entities';
import { Influencer } from '@core/models/ApiFields/ig-field-values.types';
import { Paginated } from '@core/models/IApi';
import {
    SocialComment,
    SocialCommentAnalytics,
    SocialCommentDTO,
} from '@features/inbox/inb-posts/comment.types';
import { HttpBuilder } from '@features/social-analysis/social-shared/services/http/http-builder';
import { CommentsState } from '@features/social-analysis/social-shared/store/comments.store';
import { DATA_STATUS } from '@features/social-analysis/social-shared/store/state.store';
import { finalize, map } from 'rxjs/operators';
import { IG_ENDPOINT, commentsFields } from '../config/api-params';

export class PostCommentsHttpBuilder extends HttpBuilder {
    reqComments(
        state: CommentsState,
        {
            postId,
            parentId,
            influencer,
            userAcc,
        }: {
            postId: string;
            parentId?: string;
            influencer: Influencer[];
            userAcc: string | null;
        }
    ) {
        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.buildParams(postId, userAcc, influencer, parentId);

        this.setThisIsLoading(state, parentId);
        this.http
            .get<Paginated<SocialCommentDTO[]>>(url, { params })
            .pipe(
                map(res => res.data.map(item => new SocialCommentAnalytics(item))),
                finalize(() => this.unsetIsLoading(state, parentId))
            )
            .subscribe({
                next: data =>
                    !parentId
                        ? this.handleSuccess(state, data)
                        : this.handleReplySuccess(state, data),
                error: err => this.handleError(err?.error?.message),
            });
    }

    protected buildParams(
        postId: string,
        userAcc: string | null,
        influencer: Influencer[],
        parentId?: string
    ): Params {
        return {
            ...this.getFields(),
            ...this.getPostId(postId),
            ...this.getInfluencerFilter(influencer),
            ...this.getUserAccountFilter(userAcc),
            ...this.getParentFilter(parentId),
        };
    }

    private handleReplySuccess(state: CommentsState, data: SocialComment[]) {
        state.data.update(st => [...st, ...data]);
        state.dataStatus.set(DATA_STATUS.OK);
    }

    private getFields(): Params {
        return { fields: commentsFields.join() };
    }

    private getPostId(postId: string): Params {
        return {
            [IgComment.getPrefixed('mediaId')]: 'eq:' + postId,
        };
    }

    private getParentFilter(parentId?: string): Params {
        return {
            [IgComment.getPrefixed('parentCommentId')]: parentId ? 'eq:' + parentId : null,
        };
    }

    private getInfluencerFilter(influencer: Influencer[]) {
        return {
            [IgUser.getPrefixed('influencer')]: influencer.length
                ? 'in:' + influencer.join()
                : null,
        };
    }

    private getUserAccountFilter(userAcc: string | null) {
        if (!userAcc) return {};
        return {
            [IgComment.getPrefixed('username')]: 'eq:' + userAcc,
        };
    }

    private setThisIsLoading(state: CommentsState, parentId?: string) {
        parentId ? this.addReplyIsLoading(state, parentId) : state.isLoading.set(true);
    }

    private unsetIsLoading(state: CommentsState, parentId?: string) {
        parentId ? this.removeReplyIsLoading(state, parentId) : state.isLoading.set(false);
    }

    private addReplyIsLoading(state: CommentsState, parentId: string) {
        state.isLoadingReplies.update(arr => (arr.includes(parentId) ? arr : [...arr, parentId]));
    }

    private removeReplyIsLoading(state: CommentsState, parentId: string) {
        state.isLoadingReplies.update(arr =>
            arr.includes(parentId) ? arr.filter(id => id !== parentId) : arr
        );
    }
}
